// D-193 — §7.4's missing fifth trigger: the server said so.
//
// The other four (load, `online`, every 60 s while visible, 2 s after a local write) are all the
// client *guessing* when the server might have something. This is the one case where guessing is
// unnecessary, and it is the case that hurt: two devices both sitting visible, each waiting out
// its own 60-second timer, so a document written on one took up to a minute to appear on the
// other — long enough that reloading the page is quicker, which is what people did.
//
// It is a **doorbell, not a delivery**. The event carries a sequence number and nothing else; the
// existing `SyncClient` does the pull, with D-045's conflict rules untouched. That is what keeps
// this safe to add late: the failure mode of the whole file is "sync stays as prompt as it was
// yesterday", because §7.4's poll is still running underneath it.
import type { SyncService } from './sync-service';

/** The route (§16). Relative, so it inherits the origin — which is what carries §8.2's cookie. */
export const SYNC_EVENTS_PATH = '/api/sync/events';

/**
 * How long to wait before rebuilding an `EventSource` the browser gave up on.
 *
 * `EventSource` reconnects by itself while the *connection* fails, and honours the server's
 * `retry:` hint. It does **not** reconnect when the response was refused — a non-2xx status
 * closes it permanently, which is exactly what a 429 from `MAX_STREAMS_PER_USER` is. So these
 * back off much more slowly than a network retry would: nothing is broken when they run, the
 * desktop is merely polling, and hammering a server that just said "too many" is how a refusal
 * becomes an outage.
 */
export const RECONNECT_DELAYS_MS: readonly number[] = [30_000, 60_000, 300_000];

export type SyncEventsStatus = 'stopped' | 'connecting' | 'live' | 'retrying';

export interface SyncEventsOptions {
  /** Run a sync now. The service coalesces, so calling during a run joins it. */
  readonly sync: Pick<SyncService, 'run'>;
  /**
   * The client's own cursor. A `hello` frame carrying a seq no higher than this means the desktop
   * is already current, and there is nothing to do but stay connected.
   */
  readonly cursorOf: () => number;
  /** Injected for tests; the browser's `EventSource` in production. */
  readonly create?: (url: string) => EventSourceLike;
  readonly setTimer?: (fn: () => void, ms: number) => number;
  readonly clearTimer?: (handle: number) => void;
  /** Told on every status change — the tray and §11.13's Console both want it. */
  readonly onStatus?: (status: SyncEventsStatus) => void;
}

/** The slice of `EventSource` this uses, so a test needs no DOM. */
export interface EventSourceLike {
  addEventListener(type: string, listener: (event: MessageEvent) => void): void;
  close(): void;
  readonly readyState: number;
}

export interface SyncEvents {
  /** Open the stream. Idempotent. */
  start(): void;
  /** Close it and cancel any pending retry. Idempotent. */
  stop(): void;
  status(): SyncEventsStatus;
}

const CLOSED = 2;

export function createSyncEvents(options: SyncEventsOptions): SyncEvents {
  const create =
    options.create ?? ((url: string) => new EventSource(url) as unknown as EventSourceLike);
  const setTimer =
    options.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms) as unknown as number);
  const clearTimer = options.clearTimer ?? ((handle: number) => clearTimeout(handle));

  let source: EventSourceLike | null = null;
  let retry: number | null = null;
  let attempts = 0;
  let status: SyncEventsStatus = 'stopped';
  /** Distinguishes "not started" from "started, currently between attempts". */
  let wanted = false;

  const setStatus = (next: SyncEventsStatus): void => {
    if (status === next) return;
    status = next;
    options.onStatus?.(next);
  };

  const parseSeq = (data: unknown): number | null => {
    if (typeof data !== 'string') return null;
    try {
      const parsed: unknown = JSON.parse(data);
      const seq = (parsed as { seq?: unknown }).seq;
      return typeof seq === 'number' && Number.isFinite(seq) ? seq : null;
    } catch {
      // A frame this client does not understand is not a reason to tear down the stream — the
      // next one may be perfectly good, and the poll covers anything missed either way.
      return null;
    }
  };

  function scheduleRetry(): void {
    if (!wanted || retry !== null) return;
    const delay = RECONNECT_DELAYS_MS[Math.min(attempts, RECONNECT_DELAYS_MS.length - 1)] ?? 300_000;
    attempts += 1;
    setStatus('retrying');
    retry = setTimer(() => {
      retry = null;
      open();
    }, delay);
  }

  function open(): void {
    if (!wanted || source !== null) return;
    setStatus('connecting');
    const stream = create(SYNC_EVENTS_PATH);
    source = stream;

    stream.addEventListener('open', () => {
      // Reset only on a connection that actually opened. Resetting on any event would let a
      // server that accepts and immediately closes cycle forever at the shortest delay.
      attempts = 0;
      setStatus('live');
    });

    stream.addEventListener('hello', (event) => {
      setStatus('live');
      const seq = parseSeq(event.data);
      // Strictly greater: equal means this desktop is current, and a run would be a round trip
      // to be told nothing. This is the frame that closes the gap after an outage.
      if (seq !== null && seq > options.cursorOf()) void options.sync.run();
    });

    stream.addEventListener('change', (event) => {
      setStatus('live');
      const seq = parseSeq(event.data);
      // A change this client already has is its own push echoing back: it pulls, the server logs
      // the change, the server tells it about the change it just made. Skipping those is most of
      // what keeps a busy editor from syncing on a loop.
      if (seq === null || seq > options.cursorOf()) void options.sync.run();
    });

    stream.addEventListener('error', () => {
      // `EventSource` handles a *transport* failure itself and stays open; `readyState === CLOSED`
      // is the browser saying it has given up, which is what a non-2xx response produces. Only
      // that case is ours to retry.
      if (stream.readyState !== CLOSED) {
        setStatus('connecting');
        return;
      }
      stream.close();
      if (source === stream) source = null;
      scheduleRetry();
    });
  }

  return {
    start() {
      if (wanted) return;
      wanted = true;
      attempts = 0;
      open();
    },

    stop() {
      wanted = false;
      if (retry !== null) {
        clearTimer(retry);
        retry = null;
      }
      source?.close();
      source = null;
      setStatus('stopped');
    },

    status: () => status,
  };
}
